/**
 * Build a display tree for recv order detail lines (products, deals, modifiers).
 * Shared by order detail view and manage-order receipt modal.
 */

export const normalizeType = (t) => String(t || "").trim().toUpperCase();

export const getMenuItemType = (item) =>
  item?.menuItem_Type ??
  item?.MenuItem_Type ??
  item?.menuItemType ??
  item?.MenuItemType ??
  "";

export const getMenuItemName = (item) => {
  const name =
    item?.menuItem_Name ??
    item?.MenuItem_Name ??
    item?.menuItemName ??
    item?.MenuItemName ??
    item?.reportItem_Name ??
    item?.ReportItem_Name ??
    item?.itemName ??
    item?.ItemName ??
    "";
  const trimmed = String(name || "").trim();
  if (trimmed) return trimmed;
  const menuItemId =
    item?.menuItem_ID ??
    item?.MenuItem_ID ??
    item?.menuItemId ??
    item?.MenuItemId ??
    null;
  if (menuItemId != null && String(menuItemId).trim() !== "") {
    return `Item #${menuItemId}`;
  }
  return "Unnamed";
};

export const getMenuItemQty = (item) =>
  item?.menuItem_Qty ?? item?.MenuItem_Qty ?? item?.menuItemQty ?? item?.MenuItemQty ?? 1;

export const getMenuItemPrice = (item) =>
  parseFloat(
    item?.menuItem_Price ??
      item?.MenuItem_Price ??
      item?.menuItemPrice ??
      item?.MenuItemPrice ??
      0
  ) || 0;

export const getSubTotal = (item) =>
  parseFloat(item?.subTotal ?? item?.SubTotal ?? item?.subtotal ?? 0) || 0;

export const getMenuItemDetail = (item) =>
  item?.menuItem_Detail ??
  item?.MenuItem_Detail ??
  item?.menuItemDetail ??
  item?.MenuItemDetail ??
  "";

export const getMenuItemIsModifier = (item) => {
  const v = item?.menuItem_IsModifier ?? item?.MenuItem_IsModifier;
  return v === true || v === 1 || v === "1" || v === "true";
};

/** Deal/combo slot line (included product), not toppings/variants. */
export const isDealComboSlotChild = (item) => {
  const type = normalizeType(getMenuItemType(item));
  if (type === "MODIFIER" || type === "VARIANT") return false;
  if (
    type === "DEAL_ITEM" ||
    type === "COMBO_ITEM" ||
    type === "COMBO ITEM" ||
    type === "COMBOITEM"
  ) {
    return true;
  }
  const dealId = item?.dealID ?? item?.DealID;
  const dealSlotId = item?.dealSlotID ?? item?.DealSlotID;
  if (
    dealId != null &&
    Number(dealId) > 0 &&
    dealSlotId != null &&
    Number(dealSlotId) > 0
  ) {
    return true;
  }
  const productKey = String(item?.productKey ?? item?.ProductKey ?? "")
    .trim()
    .toUpperCase();
  if (
    productKey &&
    (productKey.startsWith("DEAL_") ||
      productKey.startsWith("COMBO_") ||
      productKey.startsWith("DEAL-") ||
      productKey.startsWith("COMBO-")) &&
    !isDealOrComboHeader(item)
  ) {
    return true;
  }
  return false;
};

/** True for modifier/variant lines (including MenuItem_IsModifier flag). */
export const isModifierLine = (item) => {
  if (isDealComboSlotChild(item)) return false;
  if (getMenuItemIsModifier(item)) return true;
  const type = normalizeType(getMenuItemType(item));
  return type === "MODIFIER" || type === "VARIANT";
};

export const isModifierType = (item) => {
  const type = normalizeType(getMenuItemType(item));
  return type === "MODIFIER" || type === "VARIANT";
};

export const isVariantType = (item) => normalizeType(getMenuItemType(item)) === "VARIANT";

/** Item-level discount line (MenuItem_Type = DISCOUNT). */
export const isDiscountType = (item) => normalizeType(getMenuItemType(item)) === "DISCOUNT";

export const getDiscountAmount = (item) =>
  parseFloat(item?.discountAmount ?? item?.DiscountAmount ?? 0) || 0;

export const getDiscountPercentage = (item) => {
  const raw = item?.discountInPercentage ?? item?.DiscountInPercentage;
  const n = raw !== null && raw !== undefined ? parseFloat(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Header row only — not DEAL_ITEM / COMBO ITEM children. */
export const isDealType = (item) => {
  const type = normalizeType(getMenuItemType(item));
  return type === "DEAL" || type === "COMBO_DEAL";
};

export const isComboType = (item) => {
  const type = normalizeType(getMenuItemType(item));
  return type === "COMBO";
};

export const isDealOrComboHeader = (item) => {
  const type = normalizeType(getMenuItemType(item));
  return type === "COMBO_DEAL" || type === "DEAL" || type === "COMBO";
};

const getOrderDetailRowId = (item) => {
  const raw =
    item?.recvorderDetail_ID ??
    item?.recvorderDetailId ??
    item?.RecvorderDetail_ID ??
    item?.id ??
    item?.ID ??
    null;
  if (raw === null || raw === undefined || raw === "") return null;
  const s = String(raw).trim();
  // Empty Guid collapses every line into one root — treat as no id
  if (!s || s === "00000000-0000-0000-0000-000000000000") return null;
  return s;
};

export const getParentDetailId = (item) => {
  const raw =
    item?.recvorderDetail_ParentID ??
    item?.recvorderDetail_ParentId ??
    item?.recvorderDetailParentID ??
    item?.recvorderDetailParentId ??
    item?.parentRecvorderDetail_ID ??
    item?.parentRecvorderDetailId ??
    item?.RecvorderDetail_ParentID ??
    item?.parentID ??
    item?.parentId ??
    null;
  if (raw === null || raw === undefined || raw === "") return null;
  const s = String(raw).trim();
  return s.length > 0 ? s : null;
};

const getMenuItemId = (item) => {
  const raw =
    item?.menuItem_ID ??
    item?.MenuItem_ID ??
    item?.menuItemId ??
    item?.MenuItemId ??
    item?.menuItemID ??
    null;
  const n = raw !== null && raw !== undefined ? parseInt(raw, 10) : null;
  return Number.isFinite(n) && n > 0 ? n : null;
};

const getParentMenuItemId = (item) => {
  const raw =
    item?.parent_MenuItemID ??
    item?.Parent_MenuItemID ??
    item?.parentMenuItemId ??
    item?.ParentMenuItemId ??
    item?.parentMenuItemID ??
    null;
  const n = raw !== null && raw !== undefined ? parseInt(raw, 10) : null;
  return Number.isFinite(n) && n > 0 ? n : null;
};

const getModifierRelation = (item) => {
  const k =
    item?.modifierRelationID ??
    item?.ModifierRelationID ??
    item?.modifierRelationId ??
    item?.Modifier_RelationID ??
    "";
  const s = String(k || "").trim();
  // DB default '0' is not a real group key
  if (!s || s === "0") return "";
  return s;
};

const getProductKey = (item) => {
  const k = item?.productKey ?? item?.ProductKey ?? item?.Product_Key ?? item?.Productkey ?? "";
  const s = String(k || "").trim().toUpperCase();
  // DB default '1' is not a meaningful deal/combo instance key
  if (!s || s === "1") return "";
  return s;
};

/** Deal/combo slot children only — not regular modifiers (they also have Parent_MenuItemID). */
const isDealOrComboChild = (item) => {
  if (isDealOrComboHeader(item)) return false;

  const productKey = getProductKey(item);
  const type = normalizeType(getMenuItemType(item));

  if (type === "MODIFIER" || type === "VARIANT") return false;

  if (
    item?.isDealChild === true ||
    item?.IsDealChild === true ||
    item?.is_deal_child === true ||
    item?.dealChild === true ||
    item?.DealChild === true ||
    item?.isComboChild === true ||
    item?.IsComboChild === true ||
    item?.comboChild === true
  ) {
    return true;
  }

  if (
    productKey.startsWith("DEAL_") ||
    productKey.startsWith("COMBO_") ||
    productKey.startsWith("DEAL-") ||
    productKey.startsWith("COMBO-")
  ) {
    return true;
  }

  if (
    type === "DEAL_ITEM" ||
    type === "COMBO_ITEM" ||
    type === "COMBO ITEM" ||
    type === "COMBOITEM"
  ) {
    return true;
  }

  const dealId = item?.dealID ?? item?.DealID;
  const dealSlotId = item?.dealSlotID ?? item?.DealSlotID;
  if (
    dealId != null &&
    Number(dealId) > 0 &&
    dealSlotId != null &&
    Number(dealSlotId) > 0
  ) {
    return true;
  }

  return false;
};

/** Lines that must not appear as top-level products in the UI. */
export const isNestedOrderDetailLine = (item) => {
  if (isDiscountType(item)) return true;
  if (isModifierLine(item)) return true;
  if (isDealOrComboChild(item)) return true;
  const parentMenuItemId = getParentMenuItemId(item);
  if (parentMenuItemId && !isDealOrComboHeader(item)) return true;
  if (getParentDetailId(item)) return true;
  return false;
};

const findDealComboHeaderByProductKey = (productKey, headerByProductKey) => {
  if (!productKey || !headerByProductKey?.size) return null;
  if (headerByProductKey.has(productKey)) return headerByProductKey.get(productKey);
  for (const [headerKey, rootNode] of headerByProductKey.entries()) {
    if (
      productKey === headerKey ||
      productKey.startsWith(`${headerKey}_`) ||
      headerKey.startsWith(`${productKey}_`)
    ) {
      return rootNode;
    }
  }
  return null;
};

export const buildOrderTree = (items = []) => {
  if (!items || items.length === 0) return [];

  const nodeById = new Map();
  const nodes = [];
  const headerByProductKey = new Map();
  const headerByMenuItemId = new Map();
  const headerByDealId = new Map();
  const mainByModifierRelation = new Map();
  const itemByMenuItemId = new Map();
  const attachedChild = new Set();

  const attachChild = (parent, child) => {
    if (!parent || !child || parent === child || attachedChild.has(child)) return false;
    parent.children.push(child);
    attachedChild.add(child);
    return true;
  };

  items.forEach((it) => {
    const id = getOrderDetailRowId(it);
    const node = { id, item: it, children: [] };
    nodes.push(node);
    if (id !== null) nodeById.set(id, node);
  });

  nodes.forEach((node) => {
    const it = node.item;
    if (isDealOrComboHeader(it)) {
      const key = getProductKey(it);
      if (key) headerByProductKey.set(key, node);
      const menuItemId = getMenuItemId(it);
      if (menuItemId) headerByMenuItemId.set(menuItemId, node);
      const dealId = it?.dealID ?? it?.DealID ?? null;
      if (dealId != null && Number(dealId) > 0) {
        const dKey = String(dealId);
        if (!headerByDealId.has(dKey)) headerByDealId.set(dKey, node);
      }
    }
    // Only real products own a ModifierRelationID group (not modifiers/discounts)
    if (!isModifierLine(it) && !isDiscountType(it)) {
      const rel = getModifierRelation(it);
      if (rel) mainByModifierRelation.set(rel, node);
    }
    const menuItemId = getMenuItemId(it);
    if (menuItemId && !isDiscountType(it)) itemByMenuItemId.set(menuItemId, node);
  });

  const attachNode = (node, passModifiersOnly) => {
    const it = node.item;
    if (isDealOrComboHeader(it)) return;

    const isMod = isModifierLine(it);
    const isDisc = isDiscountType(it);
    // Discounts attach in the same pass as modifiers (after mains are linked)
    if (passModifiersOnly !== (isMod || isDisc)) return;

    const parentId = getParentDetailId(it);
    const parentMenuItemId = getParentMenuItemId(it);
    const productKey = getProductKey(it);
    const modifierRel = getModifierRelation(it);

    if (parentId !== null && nodeById.has(parentId)) {
      attachChild(nodeById.get(parentId), node);
      return;
    }

    if (isMod || isDisc) {
      if (modifierRel && mainByModifierRelation.has(modifierRel)) {
        const parentNode = mainByModifierRelation.get(modifierRel);
        if (!isDealOrComboHeader(parentNode?.item)) {
          attachChild(parentNode, node);
          return;
        }
      }
      if (parentMenuItemId !== null && itemByMenuItemId.has(parentMenuItemId)) {
        const parentNode = itemByMenuItemId.get(parentMenuItemId);
        if (!isDealOrComboHeader(parentNode?.item)) {
          attachChild(parentNode, node);
          return;
        }
      }
      return;
    }

    if (isDealOrComboChild(it)) {
      const headerFromKey = findDealComboHeaderByProductKey(productKey, headerByProductKey);
      if (headerFromKey) {
        attachChild(headerFromKey, node);
        return;
      }
      const dealId = it?.dealID ?? it?.DealID ?? null;
      if (dealId != null && headerByDealId.has(String(dealId))) {
        attachChild(headerByDealId.get(String(dealId)), node);
        return;
      }
      if (parentMenuItemId !== null) {
        if (headerByMenuItemId.has(parentMenuItemId)) {
          attachChild(headerByMenuItemId.get(parentMenuItemId), node);
          return;
        }
        const parentNode = itemByMenuItemId.get(parentMenuItemId);
        if (parentNode && isDealOrComboHeader(parentNode.item)) {
          attachChild(parentNode, node);
          return;
        }
      }
    }

    if (productKey) {
      const headerFromKey = findDealComboHeaderByProductKey(productKey, headerByProductKey);
      if (headerFromKey) {
        attachChild(headerFromKey, node);
        return;
      }
    }

    if (parentMenuItemId !== null) {
      if (headerByMenuItemId.has(parentMenuItemId)) {
        attachChild(headerByMenuItemId.get(parentMenuItemId), node);
        return;
      }
      const parentNode = itemByMenuItemId.get(parentMenuItemId);
      if (parentNode) {
        attachChild(parentNode, node);
      }
    }
  };

  // Pass 1: slot products / mains so ModifierRelationID and MenuItem_ID maps are populated
  nodes.forEach((node) => attachNode(node, false));
  // Pass 2: modifiers, variants & discounts under their deal item / product line
  nodes.forEach((node) => attachNode(node, true));

  // Fallback: attach orphan discounts to the preceding product in list order
  nodes.forEach((node, idx) => {
    if (!isDiscountType(node?.item) || attachedChild.has(node)) return;
    for (let j = idx - 1; j >= 0; j--) {
      const candidate = nodes[j];
      if (isDiscountType(candidate?.item) || isModifierLine(candidate?.item)) continue;
      attachChild(candidate, node);
      break;
    }
  });

  const roots = nodes.filter((n) => !attachedChild.has(n));

  const dealComboRoots = roots.filter((n) => isDealOrComboHeader(n?.item));
  const otherRoots = roots.filter((n) => !isDealOrComboHeader(n?.item));
  const groupedByKey = new Map();

  dealComboRoots.forEach((node) => {
    const productKey = getProductKey(node?.item);
    const menuItemId = getMenuItemId(node?.item);
    const dealName = getMenuItemName(node?.item);
    const key = productKey
      ? `pk_${productKey}`
      : menuItemId != null
        ? `deal_${menuItemId}_${node?.id}`
        : `deal_name_${dealName}_${node?.id}`;
    if (!groupedByKey.has(key)) groupedByKey.set(key, []);
    groupedByKey.get(key).push(node);
  });

  const consolidatedDealRoots = [];
  groupedByKey.forEach((group) => {
    if (group.length <= 1) {
      consolidatedDealRoots.push(...group);
      return;
    }
    const first = group[0];
    let totalQty = 0;
    let totalSubTotal = 0;
    const allChildren = [];
    group.forEach((n) => {
      totalQty += Number(getMenuItemQty(n?.item)) || 0;
      totalSubTotal += Number(getSubTotal(n?.item)) || 0;
      if (n.children?.length) allChildren.push(...n.children);
    });
    const mergedItem = {
      ...first.item,
      MenuItem_Qty: totalQty,
      menuItem_Qty: totalQty,
      MenuItemQty: totalQty,
      SubTotal: totalSubTotal,
      subTotal: totalSubTotal,
      Subtotal: totalSubTotal,
    };
    consolidatedDealRoots.push({
      id: first.id,
      item: mergedItem,
      children: allChildren.length ? allChildren : first.children || [],
    });
  });

  const mergedRoots = [...otherRoots, ...consolidatedDealRoots];
  const rootIds = new Set();

  return mergedRoots.filter((n) => {
    if (isNestedOrderDetailLine(n?.item)) return false;
    if (n.id === null) return true;
    if (rootIds.has(n.id)) return false;
    rootIds.add(n.id);
    return true;
  });
};

/** Direct modifier/variant children of a tree node. */
export const getModifierChildNodes = (node) =>
  (node?.children || []).filter((c) => isModifierLine(c?.item));

/** Nested modifier option lines under a modifier group node. */
export const getNestedModifierOptionNodes = (modNode) =>
  (modNode?.children || []).filter((c) => isModifierLine(c?.item));

/** Item-level discount children of a tree node. */
export const getDiscountChildNodes = (node) =>
  (node?.children || []).filter((c) => isDiscountType(c?.item));
